
import React, { useState, useEffect } from 'react'
import { X, FileText, Settings, Clock } from 'lucide-react'

interface TemplateContratoFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
  initialData?: any
  title: string
}

const TemplateContratoForm: React.FC<TemplateContratoFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title
}) => {
  const [formData, setFormData] = useState({
    nome: '',
    categoria: '',
    descricao: '',
    conteudo: '',
    clausulasEspeciais: '',
    condicoesGerais: '',
    prazoValidadePadrao: 30,
    ativo: true
  })

  // Initialize form with existing data
  useEffect(() => {
    if (initialData) {
      setFormData({
        nome: initialData.nome || '',
        categoria: initialData.categoria || '',
        descricao: initialData.descricao || '',
        conteudo: initialData.conteudo || '',
        clausulasEspeciais: initialData.clausulasEspeciais || '',
        condicoesGerais: initialData.condicoesGerais || '',
        prazoValidadePadrao: initialData.prazoValidadePadrao || 30,
        ativo: initialData.ativo !== undefined ? initialData.ativo : true
      })
    }
  }, [initialData])

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit(formData)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Informações Básicas */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileText size={20} className="text-gray-600" />
              Informações Básicas
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Template *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria *
                </label>
                <select
                  value={formData.categoria}
                  onChange={(e) => setFormData(prev => ({ ...prev, categoria: e.target.value }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecionar categoria...</option>
                  <option value="Prestação de Serviços">Prestação de Serviços</option>
                  <option value="Construção">Construção</option>
                  <option value="Consultoria">Consultoria</option>
                  <option value="Fornecimento">Fornecimento</option>
                  <option value="Manutenção">Manutenção</option>
                  <option value="Outros">Outros</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                  rows={2}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Configurações */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Settings size={20} className="text-gray-600" />
              Configurações
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prazo de Validade Padrão (dias)
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.prazoValidadePadrao}
                  onChange={(e) => setFormData(prev => ({ ...prev, prazoValidadePadrao: Number(e.target.value) }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.ativo ? 'ativo' : 'inativo'}
                  onChange={(e) => setFormData(prev => ({ ...prev, ativo: e.target.value === 'ativo' }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                </select>
              </div>
            </div>
          </div>

          {/* Conteúdo do Template */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Conteúdo do Template</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conteúdo Principal *
                </label>
                <textarea
                  value={formData.conteudo}
                  onChange={(e) => setFormData(prev => ({ ...prev, conteudo: e.target.value }))}
                  required
                  rows={8}
                  placeholder="Use [NOME_CLIENTE], [NOME_PROJETO], [VALOR_TOTAL], etc. como variáveis"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Variáveis disponíveis: [NOME_CLIENTE], [NOME_PROJETO], [VALOR_TOTAL], [DESCRICAO_PROJETO], [DATA_EMISSAO], [DATA_VALIDADE]
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cláusulas Especiais
                </label>
                <textarea
                  value={formData.clausulasEspeciais}
                  onChange={(e) => setFormData(prev => ({ ...prev, clausulasEspeciais: e.target.value }))}
                  rows={4}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Condições Gerais
                </label>
                <textarea
                  value={formData.condicoesGerais}
                  onChange={(e) => setFormData(prev => ({ ...prev, condicoesGerais: e.target.value }))}
                  rows={4}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {initialData ? 'Atualizar' : 'Criar'} Template
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default TemplateContratoForm
